import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero } from "@/components/common/PageHero";
import { Reveal } from "@/components/common/Reveal";
import { CTASection } from "@/components/common/CTASection";
import { Icon } from "@/components/common/Icon";
import { SERVICE_GROUPS, ENGAGEMENT_MODELS } from "@/lib/content";

export default function Services() {
  const navigate = useNavigate();
  return (
    <>
      <PageHero
        kicker="Services"
        title="Everything you need to build your India operations."
        subtitle="Four logical groups covering the full lifecycle of an India operation — each service framed around the outcome it delivers for you."
      />

      <section className="section-pad" data-testid="services-groups">
        <div className="container-px space-y-20">
          {SERVICE_GROUPS.map((g, gi) => (
            <div key={g.id} id={g.id}>
              <Reveal>
                <div className="mb-8 flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-orange-500/20 bg-orange-500/5 text-orange-400">
                    <Icon name={g.icon} className="h-6 w-6" />
                  </div>
                  <div>
                    <div className="kicker">Group {gi + 1}</div>
                    <h2 className="font-head text-2xl font-bold text-white sm:text-3xl">{g.title}</h2>
                  </div>
                </div>
              </Reveal>
              <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                {g.services.map((s, i) => (
                  <Reveal key={s.name} i={i}>
                    <div className="group h-full rounded-2xl border border-white/8 bg-white/[0.02] p-6 transition-all duration-300 hover:-translate-y-1 hover:border-orange-500/30 hover:bg-white/[0.04]">
                      <h3 className="font-head text-lg font-bold text-white group-hover:text-orange-400">{s.name}</h3>
                      <p className="mt-3 text-sm leading-relaxed text-zinc-400">{s.desc}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Engagement models */}
      <section className="section-pad border-t border-white/10 bg-[#131316]" data-testid="services-engagement">
        <div className="container-px">
          <Reveal>
            <div className="kicker mb-4 flex gap-2"><span className="h-px w-8 bg-orange-400/60" /> Engagement Models</div>
          </Reveal>
          <Reveal i={1}>
            <h2 className="max-w-3xl font-head text-3xl font-bold text-white sm:text-4xl">
              Pick a model that fits — or blend them as you grow.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {ENGAGEMENT_MODELS.map((m, i) => (
              <Reveal key={m.id} i={i}>
                <div className="flex h-full flex-col rounded-2xl border border-white/8 bg-gradient-to-b from-white/[0.04] to-transparent p-7">
                  <span className="mb-4 w-fit rounded-full border border-orange-500/20 bg-orange-500/5 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-orange-300">{m.tag}</span>
                  <h3 className="font-head text-2xl font-bold text-white">{m.name}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-400">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal i={2}>
            <button onClick={() => navigate("/contact")} className="btn-primary mt-10" data-testid="services-page-cta">
              Talk to a GCC Expert <ArrowRight className="h-4 w-4" />
            </button>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </>
  );
}
